// src/contexts/AudioPlayerContext.js
import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useRef,
  useEffect,
} from 'react';
import { loadMixcloudWidgetApi } from '../utils/loadmixcloudwidget';
import {
  MIXCLOUD_USERNAME,
  MIXCLOUD_WIDGET_IFRAME_BASE,
} from '../config/mixcloud';

const normalizeStations = (data) => {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.stations)
      ? data.stations
      : [];

  return list
    .filter((s) => s && typeof s === 'object')
    .map((s, idx) => ({ ...s, id: s.id || `station-${idx}` }))
    .filter((s) => Boolean(s.name) && Boolean(s.streamUrl));
};

const AudioPlayerContext = createContext();

export const useAudioPlayer = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error('useAudioPlayer must be used within an AudioPlayerProvider');
  }
  return context;
};

const FALLBACK_STATIONS = [];

const buildMetadataRequestUrl = (metadataUrl) => {
  if (!metadataUrl) return null;
  const baseRaw = process.env.REACT_APP_METADATA_PROXY_BASE || '';
  const base = baseRaw.replace(/\/+$/, '');
  if (!base) return metadataUrl;
  const cacheBust = `t=${Date.now()}`;
  const glue = base.includes('?') ? '&' : '?';
  return `${base}/api/metadata?url=${encodeURIComponent(metadataUrl)}&${cacheBust}${glue}`;
};

// Iframe src used for the initial mount.
const MIXCLOUD_IFRAME_INITIAL_SRC =
  `${MIXCLOUD_WIDGET_IFRAME_BASE}&feed=` +
  encodeURIComponent(`/${MIXCLOUD_USERNAME}/`);

const emptyTrackInfo = (overrides = {}) => ({
  title: '',
  start_time: null,
  artwork_urls: { standard: '', large: '' },
  track_artist: 'Utter Radio',
  track_title: '',
  track_album: 'Unknown',
  track_playout_duration: 0,
  track_duration: 0,
  ...overrides,
});

export const AudioPlayerProvider = ({ children }) => {
  // ---------- Station data ----------
  const [stations, setStations] = useState(FALLBACK_STATIONS);
  const [currentStation, setCurrentStationState] = useState(
    FALLBACK_STATIONS[0] || null
  );

  // ---------- Track info (what the global player bar shows) ----------
  const [trackInfo, setTrackInfo] = useState(emptyTrackInfo({
    track_artist: 'Utter Radio',
    track_title: 'Utter Radio',
  }));
  const [showArt, setShowArt] = useState('');
  const [showTitle, setShowTitle] = useState('');
  const [catchupShowArt, setCatchupShowArt] = useState('');

  // ---------- Live metadata snapshot (independent of catchup) ----------
  // Always reflects the CURRENT LIVE STATION's track, even while a catchup
  // episode is playing. The live radio page reads from these instead of the
  // shared trackInfo/showArt, so it never shows catchup metadata.
  const [liveTrackInfo, setLiveTrackInfo] = useState(emptyTrackInfo());
  const [liveShowArt, setLiveShowArt] = useState('');
  const [liveShowTitle, setLiveShowTitle] = useState('');

  // ---------- Metadata ----------
  const [metadata, setMetadata] = useState({});
  const [recentTracks, setRecentTracks] = useState({});
  const metadataIntervalsRef = useRef({});

  // ---------- Player ----------
  const [isPlaying, setIsPlayingState] = useState(false);
  const [volume, setVolumeState] = useState(0.8);

  // ---------- Catchup ----------
  const [isCatchupMode, setIsCatchupMode] = useState(false);
  const [catchupCurrentTime, setCatchupCurrentTime] = useState(0);
  const [catchupDuration, setCatchupDuration] = useState(0);

  // Mirror of isCatchupMode readable from stable callbacks without
  // forcing dependency-array churn through the whole metadata chain.
  const isCatchupModeRef = useRef(false);
  useEffect(() => {
    isCatchupModeRef.current = isCatchupMode;
  }, [isCatchupMode]);

  // ---------- Audio refs ----------
  const liveAudioRef = useRef(new Audio());
  const catchupAudioRef = useRef(new Audio());

  // ---------- Mixcloud refs / state ----------
  const mixcloudWidgetRef = useRef(null);
  const mixcloudIframeRef = useRef(null);
  const mixcloudProgressIntervalRef = useRef(null);
  const pendingMixcloudLoadRef = useRef(null);
  const mixcloudEndedFiredRef = useRef(false);
  const mixcloudOnEndedRef = useRef(null);
  const mixcloudAutoplayFallbackRef = useRef(null);
  const mixcloudLastKeyRef = useRef(null);

  const [mixcloudReady, setMixcloudReady] = useState(false);

  // ========================================================================
  //  METADATA
  // ========================================================================
  const fetchMetadataForStation = useCallback(async (station) => {
    if (!station?.metadataUrl) return null;
    try {
      if (station.metadataUrl.includes('example.com')) {
        return {
          data: {
            title: station.name,
            start_time: new Date().toISOString(),
            artwork_urls: {
              standard: station.showArt || '',
              large: station.showArt || '',
            },
            track_artist: 'Various Artists',
            track_title: `${station.genre} Mix`,
            track_album: `${station.genre} Collection`,
            track_playout_duration: 0,
            track_duration: 0,
          },
        };
      }
      const requestUrl =
        buildMetadataRequestUrl(station.metadataUrl) || station.metadataUrl;
      const response = await fetch(requestUrl, { cache: 'no-store' });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();
      if (data && data.success === false) return null;
      return data;
    } catch (error) {
      console.error('Failed to fetch metadata for station:', station?.id, error);
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadStations() {
      try {
        const base = process.env.PUBLIC_URL || '';
        const url = `${base}/stations.json?t=${Date.now()}`;
        const res = await fetch(url, { cache: 'no-store' });
        if (!res.ok) throw new Error(`Failed to load stations.json (${res.status})`);
        const data = await res.json();
        const list = normalizeStations(data);
        if (!cancelled) setStations(list.length ? list : FALLBACK_STATIONS);
      } catch (e) {
        console.error('Stations load error:', e);
        if (!cancelled) setStations(FALLBACK_STATIONS);
      }
    }
    loadStations();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!stations.length) return;
    const stillExists =
      currentStation && stations.some((s) => s.id === currentStation.id);
    if (!currentStation || !stillExists) {
      const next = stations[0];
      setCurrentStationState(next);

      const initialInfo = emptyTrackInfo({
        title: next?.name || '',
        artwork_urls: {
          standard: next?.showArt || '',
          large: next?.showArt || '',
        },
        track_artist: 'XMDV Radio',
        track_title: `Genre: ${next?.genre || 'Unknown'}`,
      });

      setTrackInfo(initialInfo);
      setShowArt(next?.showArt || '');
      setShowTitle(next?.name || '');

      // Seed the live snapshot too, so the live page has something to show
      // before the first metadata poll returns.
      setLiveTrackInfo(initialInfo);
      setLiveShowArt(next?.showArt || '');
      setLiveShowTitle(next?.name || '');

      const audio = liveAudioRef.current;
      if (next?.streamUrl && audio.src !== next.streamUrl) {
        audio.src = next.streamUrl;
        audio.load();
      }
    }
  }, [stations, currentStation]);

  const updateTrackInfoFromMetadata = useCallback((station, metadataObj) => {
    if (!station || !metadataObj) return;

    let newInfo;

    if (metadataObj.data) {
      const data = metadataObj.data;
      newInfo = emptyTrackInfo({
        title: data.title || station.name,
        start_time: data.start_time || null,
        artwork_urls: {
          standard: data.artwork_urls?.standard || station.showArt || '',
          large: data.artwork_urls?.large || station.showArt || '',
        },
        track_artist: data.track_artist || 'XMDV Radio',
        track_title: data.track_title || `Genre: ${station.genre}`,
        track_album: data.track_album || 'Unknown',
        track_playout_duration: data.track_playout_duration || 0,
        track_duration: data.track_duration || 0,
      });
    } else {
      const song = metadataObj.now_playing?.song;
      const show = metadataObj.live?.streamer_name || 'JukeBox';
      newInfo = emptyTrackInfo({
        title: show || station.name,
        artwork_urls: {
          standard: song?.art || station.showArt || '',
          large: song?.art || station.showArt || '',
        },
        track_artist: song?.artist || 'Various Artists',
        track_title: song ? song.title : `Genre: ${station.genre}`,
      });
    }

    // Always update the LIVE snapshot — the live radio page reads these,
    // regardless of whether catchup is currently playing.
    setLiveTrackInfo(newInfo);
    setLiveShowArt(newInfo.artwork_urls.large || newInfo.artwork_urls.standard);
    setLiveShowTitle(newInfo.title);

    // Only push to the shared "currently playing" state when we're NOT in
    // catchup mode. In catchup, the bar must keep showing the episode.
    if (!isCatchupModeRef.current) {
      setTrackInfo(newInfo);
      setShowArt(newInfo.artwork_urls.large || newInfo.artwork_urls.standard);
      setShowTitle(newInfo.title);
    }
  }, []); // stable — uses ref for isCatchupMode

  const processMetadata = useCallback(
    (station, data) => {
      if (!station || !data) return;
      setMetadata((prev) => ({ ...prev, [station.id]: data }));

      if (data?.data) {
        const history = data.song_history || [];
        if (Array.isArray(history)) {
          setRecentTracks((prev) => ({
            ...prev,
            [station.id]: history.slice(0, 4),
          }));
        }
      } else if (data?.song_history && Array.isArray(data.song_history)) {
        setRecentTracks((prev) => ({
          ...prev,
          [station.id]: data.song_history.slice(0, 4),
        }));
      }

      // No isCatchupMode guard — updateTrackInfoFromMetadata decides whether
      // to write to shared state or only to the live snapshot.
      if (currentStation?.id === station.id) {
        updateTrackInfoFromMetadata(station, data);
      }
    },
    [currentStation, updateTrackInfoFromMetadata]
  );

  const loadMetadataForStation = useCallback(
    async (station) => {
      const data = await fetchMetadataForStation(station);
      if (data) processMetadata(station, data);
      return data;
    },
    [fetchMetadataForStation, processMetadata]
  );

  const startMetadataPolling = useCallback(
    (station) => {
      if (!station) return;
      if (metadataIntervalsRef.current[station.id]) {
        clearInterval(metadataIntervalsRef.current[station.id]);
      }
      loadMetadataForStation(station);
      const intervalMs = Number.isFinite(station.metadataPollIntervalMs)
        ? station.metadataPollIntervalMs
        : station.id === 'utter-radio'
          ? 2000
          : 15000;
      if (intervalMs > 0 && station.metadataUrl) {
        metadataIntervalsRef.current[station.id] = setInterval(() => {
          loadMetadataForStation(station);
        }, intervalMs);
      }
    },
    [loadMetadataForStation]
  );

  const stopAllMetadataPolling = useCallback(() => {
    Object.values(metadataIntervalsRef.current).forEach(clearInterval);
    metadataIntervalsRef.current = {};
  }, []);

  // ========================================================================
  //  LIVE AUDIO
  // ========================================================================
  useEffect(() => {
    const audio = liveAudioRef.current;
    audio.volume = volume;
    audio.preload = 'none';

    const handlePlay = () => {
      setIsPlayingState(true);
      if (isCatchupMode) {
        setIsCatchupMode(false);
        catchupAudioRef.current.pause();
        const widget = mixcloudWidgetRef.current;
        if (widget && typeof widget.pause === 'function') {
          try { widget.pause(); } catch (e) { /* ignore */ }
        }
      }
    };
    const handlePause = () => setIsPlayingState(false);
    const handleEnded = () => setIsPlayingState(false);

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [isCatchupMode, volume]);

  // ========================================================================
  //  LEGACY CATCHUP AUDIO (fallback when the widget can't be used)
  // ========================================================================
  useEffect(() => {
    const audio = catchupAudioRef.current;
    audio.volume = volume;
    audio.preload = 'none';

    const handlePlay = () => {
      setIsPlayingState(true);
      setIsCatchupMode(true);
      if (!liveAudioRef.current.paused) {
        liveAudioRef.current.pause();
        // NOTE: intentionally do NOT stop metadata polling here. Polling
        // keeps the live snapshot fresh while catchup is playing.
      }
    };
    const handlePause = () => setIsPlayingState(false);
    const handleTimeUpdate = () => setCatchupCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setCatchupDuration(audio.duration);

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [volume]);

  // Start metadata polling whenever the current station changes. Polling
  // continues during catchup so liveTrackInfo stays fresh.
  useEffect(() => {
    if (!currentStation) return;
    startMetadataPolling(currentStation);
    return () => { stopAllMetadataPolling(); };
  }, [currentStation, startMetadataPolling, stopAllMetadataPolling]);

  useEffect(() => {
    liveAudioRef.current.volume = volume;
    catchupAudioRef.current.volume = volume;
    const widget = mixcloudWidgetRef.current;
    if (widget && typeof widget.setVolume === 'function') {
      try { widget.setVolume(volume); } catch (e) { /* ignore */ }
    }
  }, [volume, mixcloudReady]);

  // ========================================================================
  //  STATION CONTROL
  // ========================================================================
  const setCurrentStation = useCallback(
    (station, autoplay = false) => {
      if (!station || !station.streamUrl) return;

      if (isCatchupMode) {
        catchupAudioRef.current.pause();
        setIsCatchupMode(false);
      }

      setCurrentStationState(station);

      const newInfo = emptyTrackInfo({
        title: station.name || '',
        artwork_urls: {
          standard: station.showArt || '',
          large: station.showArt || '',
        },
        track_artist: 'XMDV Radio',
        track_title: `Genre: ${station.genre || 'Unknown'}`,
      });

      setTrackInfo(newInfo);
      setShowArt(station.showArt || '');
      setShowTitle(station.name || '');

      const audio = liveAudioRef.current;
      if (audio.src !== station.streamUrl) {
        audio.src = station.streamUrl;
        audio.load();
      }
      if (autoplay) audio.play().catch(console.error);
      startMetadataPolling(station);
    },
    [isCatchupMode, startMetadataPolling]
  );

  // ========================================================================
  //  MIXCLOUD WIDGET
  // ========================================================================
  const attachMixcloudWidget = useCallback((iframe) => {
    if (!iframe) return;

    console.debug('[AudioPlayer] attachMixcloudWidget');

    mixcloudWidgetRef.current = null;
    setMixcloudReady(false);

    mixcloudIframeRef.current = iframe;

    loadMixcloudWidgetApi()
      .then(() => {
        if (!window.Mixcloud) {
          console.warn('[AudioPlayer] window.Mixcloud missing after load');
          return;
        }
        try {
          const widget = window.Mixcloud.PlayerWidget(iframe);
          widget.ready.then(() => {
            mixcloudWidgetRef.current = widget;
            setMixcloudReady(true);
            console.debug('[AudioPlayer] Mixcloud widget ready');

            if (typeof widget.setVolume === 'function') {
              try { widget.setVolume(volume); } catch (e) { /* ignore */ }
            }

            const queued = pendingMixcloudLoadRef.current;
            if (queued) {
              pendingMixcloudLoadRef.current = null;
              widget
                .load(queued.cloudcastKey, queued.startPlaying)
                .catch((e) =>
                  console.error('[AudioPlayer] queued widget.load failed:', e)
                );
            }
          });
        } catch (e) {
          console.error('[AudioPlayer] Failed to create Mixcloud widget:', e);
        }
      })
      .catch((e) =>
        console.error('[AudioPlayer] Failed to load Mixcloud widget API:', e)
      );
  }, [volume]);

  const handleMixcloudIframeRef = useCallback((el) => {
    mixcloudIframeRef.current = el;
  }, []);

  const handleMixcloudIframeLoad = useCallback(
    (e) => {
      const iframe = e?.target || e?.currentTarget || mixcloudIframeRef.current;
      if (!iframe) return;
      attachMixcloudWidget(iframe);
    },
    [attachMixcloudWidget]
  );

  const setMixcloudOnEnded = useCallback((cb) => {
    mixcloudOnEndedRef.current = typeof cb === 'function' ? cb : null;
  }, []);

  const forceMixcloudAutoplay = useCallback((cloudcastKey) => {
    const iframe = mixcloudIframeRef.current;
    if (!iframe) return;

    const newSrc =
      `${MIXCLOUD_WIDGET_IFRAME_BASE}&autoplay=1&feed=` +
      encodeURIComponent(cloudcastKey);

    console.debug('[AudioPlayer] forcing autoplay iframe src:', newSrc);

    pendingMixcloudLoadRef.current = {
      cloudcastKey,
      startPlaying: true,
    };

    iframe.src = newSrc;
  }, []);

  const loadMixcloudEpisode = useCallback(
    async (cloudcastKey, episodeTitle, showTitle, showArt, startPlaying = true) => {
      console.debug('[AudioPlayer] loadMixcloudEpisode:', {
        cloudcastKey,
        mixcloudReady,
        hasWidget: !!mixcloudWidgetRef.current,
      });

      // Stop live audio (but NOT metadata polling — live snapshot keeps
      // refreshing in the background).
      if (!liveAudioRef.current.paused) liveAudioRef.current.pause();
      try { catchupAudioRef.current.pause(); } catch (e) { /* ignore */ }

      setIsCatchupMode(true);
      setCatchupCurrentTime(0);
      setCatchupDuration(0);
      mixcloudEndedFiredRef.current = false;

      mixcloudLastKeyRef.current = cloudcastKey;

      if (mixcloudAutoplayFallbackRef.current) {
        clearTimeout(mixcloudAutoplayFallbackRef.current);
        mixcloudAutoplayFallbackRef.current = null;
      }

      // UI: episode metadata goes into the SHARED trackInfo (the bar).
      // liveTrackInfo is left alone and keeps reflecting the live station.
      setCatchupShowArt(showArt || '');
      setShowTitle(showTitle || '');
      setShowArt(showArt || '');
      setTrackInfo({
        title: showTitle || 'Catchup Show',
        artwork_urls: {
          standard: showArt || '',
          large: showArt || '',
        },
        track_artist: showTitle || 'Catchup',
        track_title: episodeTitle || 'Catchup Episode',
      });

      if (startPlaying) setIsPlayingState(true);

      const widget = mixcloudWidgetRef.current;
      if (!widget) {
        pendingMixcloudLoadRef.current = { cloudcastKey, startPlaying };
        console.debug(
          '[AudioPlayer] Widget not ready, queued load:',
          cloudcastKey
        );
        return;
      }

      try {
        await widget.load(cloudcastKey, startPlaying);
        console.debug('[AudioPlayer] widget.load() resolved for', cloudcastKey);

        if (startPlaying) {
          mixcloudAutoplayFallbackRef.current = setTimeout(async () => {
            if (mixcloudLastKeyRef.current !== cloudcastKey) return;

            try {
              const [pos, paused] = await Promise.all([
                widget.getPosition(),
                widget.getIsPaused(),
              ]);
              console.debug('[AudioPlayer] autoplay check:', { pos, paused });

              if (paused === false || (typeof pos === 'number' && pos > 0.5)) {
                return;
              }
              console.warn(
                '[AudioPlayer] widget.load() did not start playback — forcing autoplay iframe'
              );
              forceMixcloudAutoplay(cloudcastKey);
            } catch (e) {
              console.error('[AudioPlayer] autoplay check failed:', e);
              forceMixcloudAutoplay(cloudcastKey);
            }
          }, 1800);
        }
      } catch (e) {
        console.error('[AudioPlayer] widget.load failed:', e);
        forceMixcloudAutoplay(cloudcastKey);
      }
    },
    [forceMixcloudAutoplay]
  );

  // ========================================================================
  //  MIXCLOUD PROGRESS POLLING
  // ========================================================================
  useEffect(() => {
    if (!mixcloudReady || !isCatchupMode) return;
    const widget = mixcloudWidgetRef.current;
    if (!widget) return;

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const [pos, dur, paused] = await Promise.all([
          widget.getPosition(),
          widget.getDuration(),
          widget.getIsPaused(),
        ]);
        if (cancelled) return;

        if (typeof pos === 'number' && !Number.isNaN(pos)) {
          setCatchupCurrentTime(pos);
        }
        if (typeof dur === 'number' && !Number.isNaN(dur) && dur > 0) {
          setCatchupDuration(dur);
        }
        if (typeof paused === 'boolean') {
          setIsPlayingState(!paused);
        }

        if (
          typeof pos === 'number' &&
          typeof dur === 'number' &&
          dur > 0 &&
          pos >= dur - 0.5 &&
          !mixcloudEndedFiredRef.current
        ) {
          mixcloudEndedFiredRef.current = true;
          if (typeof mixcloudOnEndedRef.current === 'function') {
            try { mixcloudOnEndedRef.current(); }
            catch (e) { console.error('[AudioPlayer] onEnded error:', e); }
          }
        }
      } catch (e) {
        // Widget transitioning — ignore
      }
    };

    poll();
    mixcloudProgressIntervalRef.current = setInterval(poll, 500);

    return () => {
      cancelled = true;
      if (mixcloudProgressIntervalRef.current) {
        clearInterval(mixcloudProgressIntervalRef.current);
        mixcloudProgressIntervalRef.current = null;
      }
    };
  }, [mixcloudReady, isCatchupMode]);

  // ========================================================================
  //  PLAYBACK CONTROLS
  // ========================================================================
  const togglePlayback = useCallback(() => {
    if (isCatchupMode) {
      if (mixcloudReady && mixcloudWidgetRef.current) {
        setIsPlayingState((p) => !p);
        mixcloudWidgetRef.current.togglePlay().catch(console.error);
        return;
      }
      const audio = catchupAudioRef.current;
      if (audio.paused) audio.play().catch(console.error);
      else audio.pause();
      return;
    }

    if (!currentStation) return;
    const audio = liveAudioRef.current;
    if (audio.paused) {
      audio.pause();
      audio.src = `${currentStation.streamUrl}?t=${Date.now()}`;
      audio.load();
      audio.play().catch(console.error);
    } else {
      audio.pause();
    }
  }, [isCatchupMode, currentStation, mixcloudReady]);

  const playCatchup = useCallback(() => {
    setIsCatchupMode(true);
    if (!liveAudioRef.current.paused) {
      liveAudioRef.current.pause();
    }
    if (mixcloudReady && mixcloudWidgetRef.current) {
      mixcloudWidgetRef.current.play().catch(console.error);
      return;
    }
    const audio = catchupAudioRef.current;
    if (audio.src) audio.play().catch(console.error);
  }, [mixcloudReady]);

  const switchToLiveRadio = useCallback(
    (station) => {
      if (!station || !station.streamUrl) {
        if (currentStation && currentStation.streamUrl) {
          setCurrentStation(currentStation, true);
        }
        return;
      }
      if (isCatchupMode) {
        catchupAudioRef.current.pause();
        setIsCatchupMode(false);
        const widget = mixcloudWidgetRef.current;
        if (widget && typeof widget.pause === 'function') {
          try { widget.pause(); } catch (e) { /* ignore */ }
        }
      }
      setCurrentStation(station, true);
    },
    [isCatchupMode, currentStation, setCurrentStation]
  );

  const loadCatchupEpisode = useCallback(
    (episodeUrl, episodeTitle, showTitleArg, showArtArg, onEndedCallback) => {
      if (!liveAudioRef.current.paused) {
        liveAudioRef.current.pause();
      }
      const audio = catchupAudioRef.current;
      audio.src = episodeUrl;
      audio.load();
      if (typeof onEndedCallback === 'function') {
        audio.onended = () => {
          setIsPlayingState(false);
          onEndedCallback();
        };
      }
      setIsCatchupMode(true);
      setCatchupShowArt(showArtArg || '');
      setShowTitle(showTitleArg || '');
      setShowArt(showArtArg || '');
      setTrackInfo({
        title: showTitleArg || 'Catchup Show',
        artwork_urls: {
          standard: showArtArg || '',
          large: showArtArg || '',
        },
        track_artist: showTitleArg || 'Catchup',
        track_title: episodeTitle || 'Catchup Episode',
      });
    },
    []
  );

  // ========================================================================
  //  HELPERS
  // ========================================================================
  const updateTrackInfo = useCallback((newTrackInfo) => {
    if (!newTrackInfo) return;
    setTrackInfo((prev) => ({
      ...prev,
      ...newTrackInfo,
      artwork_urls: newTrackInfo.artwork_urls
        ? { ...prev.artwork_urls, ...newTrackInfo.artwork_urls }
        : prev.artwork_urls,
    }));
    if (newTrackInfo.title) setShowTitle(newTrackInfo.title);
    if (newTrackInfo.artwork_urls?.large) {
      setShowArt(newTrackInfo.artwork_urls.large);
    } else if (newTrackInfo.artwork_urls?.standard) {
      setShowArt(newTrackInfo.artwork_urls.standard);
    }
  }, []);

  const setVolume = useCallback((newVolume) => {
    setVolumeState(Math.max(0, Math.min(1, newVolume)));
  }, []);

  const skipForward = useCallback(
    async (seconds = 10) => {
      if (isCatchupMode && mixcloudReady && mixcloudWidgetRef.current) {
        try {
          const [pos, dur] = await Promise.all([
            mixcloudWidgetRef.current.getPosition(),
            mixcloudWidgetRef.current.getDuration(),
          ]);
          const target = Math.min(
            (typeof pos === 'number' ? pos : 0) + seconds,
            typeof dur === 'number' && dur > 0 ? dur : Infinity
          );
          await mixcloudWidgetRef.current.seek(target);
        } catch (e) {
          console.error('[AudioPlayer] skipForward failed:', e);
        }
        return;
      }
      if (isCatchupMode && catchupAudioRef.current) {
        catchupAudioRef.current.currentTime = Math.min(
          catchupAudioRef.current.currentTime + seconds,
          catchupAudioRef.current.duration
        );
      }
    },
    [isCatchupMode, mixcloudReady]
  );

  const skipBackward = useCallback(
    async (seconds = 10) => {
      if (isCatchupMode && mixcloudReady && mixcloudWidgetRef.current) {
        try {
          const pos = await mixcloudWidgetRef.current.getPosition();
          const target = Math.max((typeof pos === 'number' ? pos : 0) - seconds, 0);
          await mixcloudWidgetRef.current.seek(target);
        } catch (e) {
          console.error('[AudioPlayer] skipBackward failed:', e);
        }
        return;
      }
      if (isCatchupMode && catchupAudioRef.current) {
        catchupAudioRef.current.currentTime = Math.max(
          catchupAudioRef.current.currentTime - seconds,
          0
        );
      }
    },
    [isCatchupMode, mixcloudReady]
  );

  const seekToPosition = useCallback(
    async (timeInSeconds) => {
      if (!isCatchupMode) return;
      if (mixcloudReady && mixcloudWidgetRef.current) {
        try {
          await mixcloudWidgetRef.current.seek(timeInSeconds);
          setCatchupCurrentTime(timeInSeconds);
        } catch (e) {
          console.error('[AudioPlayer] seekToPosition failed:', e);
        }
        return;
      }
      if (!catchupAudioRef.current) return;
      const audio = catchupAudioRef.current;
      const duration = Number.isFinite(audio.duration)
        ? audio.duration
        : catchupDuration;
      if (!duration || !Number.isFinite(duration)) return;
      const clamped = Math.max(0, Math.min(duration, timeInSeconds));
      audio.currentTime = clamped;
      setCatchupCurrentTime(clamped);
    },
    [isCatchupMode, mixcloudReady, catchupDuration]
  );

  const getTrackDisplayString = useCallback(() => {
    const artist = trackInfo?.track_artist;
    const title = trackInfo?.track_title;
    if (artist && title) return `${artist} - ${title}`;
    if (title) return title;
    return 'No track information';
  }, [trackInfo]);

  const getLiveTrackDisplayString = useCallback(() => {
    const artist = liveTrackInfo?.track_artist;
    const title = liveTrackInfo?.track_title;
    if (artist && title) return `${artist} - ${title}`;
    if (title) return title;
    return 'No track information';
  }, [liveTrackInfo]);

  // ========================================================================
  //  CONTEXT VALUE
  // ========================================================================
  const value = {
    stations,
    currentStation,
    isPlaying,
    volume,
    trackInfo,
    showArt,
    catchupShowArt,
    showTitle,

    // Live metadata snapshot — always reflects the current live station,
    // even while a catchup episode is playing. Live radio page should read
    // these instead of trackInfo/showArt.
    liveTrackInfo,
    liveShowArt,
    liveShowTitle,

    isCatchupMode,
    catchupCurrentTime,
    catchupDuration,

    metadata,
    recentTracks,

    togglePlayback,
    setVolume,
    setCurrentStation,
    switchToLiveRadio,
    loadCatchupEpisode,
    playCatchup,
    pauseCatchup: () => {
      if (isCatchupMode) {
        if (mixcloudReady && mixcloudWidgetRef.current) {
          try { mixcloudWidgetRef.current.pause(); } catch (e) { /* ignore */ }
        } else if (catchupAudioRef.current) {
          catchupAudioRef.current.pause();
        }
      }
    },
    skipForward,
    skipBackward,
    seekToPosition,

    updateTrackInfo,

    loadMixcloudEpisode,
    setMixcloudOnEnded,
    mixcloudReady,

    getTrackInfo: () => trackInfo,
    getShowArt: () => showArt,
    getShowTitle: () => showTitle,
    getTrackDisplayString,

    // Live-page getters
    getLiveTrackInfo: () => liveTrackInfo,
    getLiveShowArt: () => liveShowArt,
    getLiveShowTitle: () => liveShowTitle,
    getLiveTrackDisplayString,

    fetchMetadataForStation: (stationId) => {
      const station = stations.find((s) => s.id === stationId);
      if (station) return loadMetadataForStation(station);
      return null;
    },
  };

  // ========================================================================
  //  RENDER — children + persistent Mixcloud iframe
  // ========================================================================
  return (
    <AudioPlayerContext.Provider value={value}>
      {children}

      {/* Persistent Mixcloud widget iframe (off-screen but rendered). */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          left: '-10000px',
          top: 0,
          width: '400px',
          height: '180px',
          overflow: 'hidden',
          zIndex: -1,
        }}
      >
        <iframe
          ref={handleMixcloudIframeRef}
          id="mixcloud-widget"
          title="Mixcloud Player"
          src={MIXCLOUD_IFRAME_INITIAL_SRC}
          width="400"
          height="180"
          frameBorder="0"
          allow="autoplay; encrypted-media; fullscreen; idle-detection; speaker-selection"
          onLoad={handleMixcloudIframeLoad}
          style={{ border: 0 }}
        />
      </div>
    </AudioPlayerContext.Provider>
  );
};